import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CurrencyFormat from 'react-currency-format';
import { Form, Formik } from 'formik';
import get from 'lodash/get';
import moment from 'moment';
import classNames from 'classnames';
import PortalSweetAlert from './PortalSweetAlert';
import CommonButton from './CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import {
  acceptBySeller,
  cancelAuction,
  deleteAuction,
  publishAuction,
  rejectBySeller,
  republishAuction,
} from '../../myvehicles/MyVehiclesActions';
import {
  clearPhotosInServiceHistory,
  editAuction,
  fetchOrganizationLocations,
} from '../../addvehicle/AddVehicleActions';
import { preparePriceFormat } from '../helpers/preparePriceFormat';
import closeIcon from '../../assets/img/icons/close.svg';
import { constantToSelect } from '../helpers/constantToSelect';
import {
  AuctionStatusType,
  AuctionStatusTypeName,
  AvailableAuctionStatuses,
} from '../../constants/AuctionStatusType';

import { AuctionStatus, AuctionStatusName } from '../../constants/AuctionStatus';
import FormSelectField from './formFields/FormSelectField';
import { AuctionDateTimePicker } from '../../addvehicle/forms/LaunchVehicle';
import FormCheckboxFieldFixed from './formFields/FormCheckboxFieldFixed';
import FormCurrencyInputFieldFixed from './formFields/FormCurrencyInputFieldFixed';
import poundIcon from '../../assets/img/icons/poundicon.svg';
import RouterPaths from '../../constants/RouterPaths';
import { isDateBefore } from '../utils/date/Date.utils';
import checkWhiteIcon from '../../assets/img/icons/check-white.svg';
import closeWhiteIcon from '../../assets/img/icons/close-white.svg';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { SSE_SELLER, SSEClient } from '../../api/SSEClient';

const {
  REJECTED: OFFER_DECLINED_STATUS_NAME,
  CANCELLED: CANCELLED_STATUS_NAME,
  ENDED: ENDED_STATUS_NAME,
} = AuctionStatusName;
const { CANCELLED: CANCELLED_STATUS, ENDED: ENDED_STATUS } = AuctionStatus;

const sseClientSeller = new SSEClient(SSE_SELLER);

function AuctionDetailsSellerSection({
  item,
  refreshItem,
  bidNotification,
  status,
  changeStatus,
  isAuctionRelaunched,
  setIsAuctionRelaunched,
  nextStep,
  setIsRepublish,
}) {
  const dispatch = useDispatch();
  const { id: auctionId } = useParams();
  const isOwner = useSelector((state) => state.auth.get('isOwner'));
  const id = useSelector((state) => state.auth.get('id'));
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const isSseRefresh = useSelector((state) => state.addVehicle.get('isSseRefresh'));
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [highestBidAmount, setHighestBidAmount] = useState();

  const isMobile = useMediaQuery('(max-width: 576px)');

  useEffect(() => {
    if (status) {
      changeStatus(false);
      refreshItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    let highestBid = null;
    if (bidNotification) {
      setHighestBidAmount(bidNotification);
    } else if (item && item.bids.length) {
      highestBid =
        item?.proxyBidInPence ?? item?.bids?.find((bid) => bid.isHighest)?.amountInPence;
      setHighestBidAmount(preparePriceFormat(highestBid, true));
    } else if (isAuctionRelaunched) {
      setHighestBidAmount('');
    } else {
      setHighestBidAmount(bidNotification);
    }
  }, [item, bidNotification, isAuctionRelaunched]);

  const handlePublishAuction = () => {
    nextStep((prevStep) => prevStep + 1);
    setIsRepublish(false);
  };

  const handleCancelAuction = () => {
    dispatch(
      cancelAuction(item, (data) => {
        refreshItem(data);
      }),
    );
  };

  const handleAcceptAuction = () => {
    dispatch(
      acceptBySeller(item, (data) => {
        refreshItem(data);
      }),
    );
  };

  const handleRelaunchAuction = () => {
    // setIsAuctionRelaunched(true);
    // nextStep((prevStep) => prevStep + 1);
    // setIsRepublish(true);
    setIsRelaunchPopupVisible(true);
  };

  const handleRejectAuction = () => {
    dispatch(
      rejectBySeller(item, (data) => {
        refreshItem(data);
      }),
    );
  };

  const displayBidAmountOrDash = (bid) =>
    bid ? (
      <CurrencyFormat
        value={bid}
        thousandSeparator={true}
        allowNegative={false}
        prefix="£"
        displayType="text"
      />
    ) : (
      <div>-</div>
    );

  const [isRelaunchPopupVisible, setIsRelaunchPopupVisible] = useState(false);
  // Dynamically add/remove a class to <body> for CSS z-index control
  useLayoutEffect(() => {
    if (isRelaunchPopupVisible || confirmCancel || confirmDelete) {
      document.body.classList.add('relaunch-popup-active');
    } else {
      document.body.classList.remove('relaunch-popup-active');
    }
    return () => {
      document.body.classList.remove('relaunch-popup-active');
    };
  }, [isRelaunchPopupVisible, confirmCancel, confirmDelete]);
  const organizationLocations = useSelector((state) =>
    state.addVehicle.get('organizationLocations'),
  );

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
  }, [dispatch]);

  const preparedOrganizationLocations = useMemo(
    () =>
      organizationLocations?.map((item) => ({
        value: item?.id,
        label: item?.name || item?.address || item?.postalCode?.postcode,
      })) || [],
    [organizationLocations],
  );

  const auctionTypes = useMemo(
    () =>
      constantToSelect(
        Object.keys(AuctionStatusType)
          .filter((el) => AvailableAuctionStatuses.includes(el))
          .reduce(
            (obj, key) => ({
              ...obj,
              [key]: AuctionStatusTypeName[key],
            }),
            {},
          ),
      ),
    [],
  );

  const navigate = useNavigate();

  const _handleLunchOrRelaunchAuction = (values, isRelaunch = false) => {
    if (isRelaunch) {
      dispatch(
        republishAuction(
          values,
          (data) => {
            if (sseClientSeller?.client?.readyState !== 1) {
              setTimeout(() => {
                navigate(`${RouterPaths.MY_VEHICLES}`);
              }, 1000);
            }
            refreshItem(data);
          },
          false,
        ),
      );
    } else {
      dispatch(
        publishAuction(
          values,
          (data) => {
            if (sseClientSeller?.client?.readyState !== 1) {
              setTimeout(() => {
                navigate(`${RouterPaths.MY_VEHICLES}`);
              }, 1000);
            }
            refreshItem(data);
          },
          false,
        ),
      );
    }
  };

  const _renderLaunchOrRelaunchPopup = (isRelaunch = false) => {
    const restValues = {
      id: item?.id,
      minimumPriceInPence: item?.minimumPriceInPence,
      tryingToDealDeliveredOn: item?.tryingToDealDeliveredOn,
      tryingToDealDeliveredDateType: item?.tryingToDealDeliveredDateType,
      publishTarget: item?.publishTarget,
      version: item?.version,
    };
    const initialValues = {
      locationId: {
        value: item?.location?.id,
        label:
          item?.location?.name ||
          item?.location?.address ||
          item?.location?.postalCode?.postcode,
      },
      endsOn: '',
      standardAuctionAndBuyNow: item?.type !== AuctionStatusType.BUY_NOW_ONLY,
      type: {
        value: item?.type,
        label:
          // eslint-disable-next-line no-nested-ternary
          item?.type !== AuctionStatusType.BUY_NOW_ONLY
            ? item?.type === AuctionStatusType.PHYSICAL
              ? 'Reserve'
              : 'No Reserve'
            : '',
      },
      autoAcceptPriceInPence: item.autoAcceptPriceInPence / 100,
      isBuyNowFeatureEnable: !!item?.isBuyNowFeatureEnable,
      buyNowPriceInPence: item.buyNowPriceInPence / 100,
      minimumPriceInPence: item.minimumPriceInPence / 100,
    };

    return (
      <PortalSweetAlert
        show={isRelaunchPopupVisible}
        title=""
        customClass="relaunch-alert"
        showConfirm={false}
        showCloseButton={false}
        onConfirm={() => null}
      >
        <div className="sweet-alert__header">
          <span className="sweet-alert-text">
            {isRelaunch ? 'Relaunch' : 'Launch'} Auction
          </span>
          <button type="button" onClick={() => setIsRelaunchPopupVisible(false)}>
            <img src={closeIcon} alt="Close icon" />
          </button>
        </div>
        <div className="sweet-alert__content">
          <span>
            {isRelaunch ? 'Relaunch' : 'Launch'} the auction immediately with the following
            settings:
          </span>
          <div className="w-100">
            <Formik
              initialValues={initialValues}
              onSubmit={async (values, form) => {
                const endsOnObject = moment(values?.endsOn);
                const formattedDate = endsOnObject.format('YYYY-MM-DDTHH:mm:ssZ');
                let typeValue;
                if (values.standardAuctionAndBuyNow && values.isBuyNowFeatureEnable) {
                  typeValue = values.type.value;
                } else if (values.isBuyNowFeatureEnable) {
                  typeValue = 'BUY_NOW_ONLY';
                } else {
                  typeValue = values.type.value || 'PHYSICAL';
                }

                const autoPriceInPence =
                  typeValue === 'PHYSICAL' ? values.autoAcceptPriceInPence : null;
                const buyNowPrice = values.isBuyNowFeatureEnable
                  ? values.buyNowPriceInPence
                  : null;
                const minimumPriceInPence = values.standardAuctionAndBuyNow
                  ? values.minimumPriceInPence
                  : null;

                await _handleLunchOrRelaunchAuction(
                  {
                    ...values,
                    ...restValues,
                    locationId: values?.locationId?.value,
                    endsOn: formattedDate,
                    type: typeValue,
                    autoAcceptPriceInPence: autoPriceInPence,
                    buyNowPriceInPence: buyNowPrice,
                    minimumPriceInPence,
                  },
                  isRelaunch,
                );
                form.setSubmitting(false);
              }}
              enableReinitialize
            >
              {(form) => (
                <Form>
                  <Row className="mt-3">
                    <Col md={6}>
                      <FormSelectField
                        label="Location"
                        name="locationId"
                        options={preparedOrganizationLocations}
                        labelVariant="bordered"
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <AuctionDateTimePicker
                        label="Date & Time – Auction Ends"
                        name="endsOn"
                        labelVariant="bordered"
                        form={form}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Row>
                      <Col sm={12} className="mb-3">
                        <FormCheckboxFieldFixed
                          name="standardAuctionAndBuyNow"
                          label="STANDARD AUCTION"
                          form={form}
                          className="launch-vehicle-checkbox launch-vehicle-checkbox-setting"
                        />
                      </Col>
                    </Row>
                    <Col md={6}>
                      <FormSelectField
                        name="type"
                        label="Auction type"
                        options={auctionTypes}
                        labelVariant="bordered"
                        form={form}
                        disabled={!get(form.values, 'standardAuctionAndBuyNow')}
                        required={get(form.values, 'standardAuctionAndBuyNow')}
                      />
                    </Col>
                    {get(form.values, 'type')?.value === AuctionStatusType.PHYSICAL && (
                      <Col md={6}>
                        <FormCurrencyInputFieldFixed
                          name="autoAcceptPriceInPence"
                          label="Reserve Price"
                          maxLength={14}
                          labelVariant="bordered"
                          form={form}
                          icon={poundIcon}
                          iconClassName="calendar-icon"
                          disabled={!get(form.values, 'standardAuctionAndBuyNow')}
                          required={get(form.values, 'standardAuctionAndBuyNow')}
                        />
                      </Col>
                    )}
                    <Col md={6}>
                      <FormCurrencyInputFieldFixed
                        name="minimumPriceInPence"
                        label="Minimum Price"
                        maxLength={14}
                        labelVariant="bordered"
                        form={form}
                        icon={poundIcon}
                        iconClassName="calendar-icon"
                        disabled={!get(form.values, 'standardAuctionAndBuyNow')}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12} className="mb-1">
                      <FormCheckboxFieldFixed
                        className="launch-vehicle-checkbox launch-vehicle-checkbox-setting"
                        name="isBuyNowFeatureEnable"
                        label="BUY NOW OFFER"
                        form={form}
                      />
                    </Col>
                    <Col md={6} className="mt-3">
                      <FormCurrencyInputFieldFixed
                        name="buyNowPriceInPence"
                        label="Buy Now Price"
                        maxLength={14}
                        labelVariant="bordered"
                        form={form}
                        icon={poundIcon}
                        placeholder="Miniumum £150"
                        iconClassName="calendar-icon"
                        disabled={!get(form.values, 'isBuyNowFeatureEnable')}
                        required={get(form.values, 'isBuyNowFeatureEnable')}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col md={6} className="mb-4 mb-md-0">
                      <CommonButton
                        type="submit"
                        label={`${isRelaunch ? 'Relaunch' : 'Launch'} auction`}
                        variant={CommonButtonVariants.PRIMARY_RED}
                        className="w-100 common-button red-button"
                        disabled={
                          isDateBefore(get(form.values, 'endsOn'), new Date()) ||
                          !get(form.values, 'locationId') ||
                          !get(form.values, 'endsOn') ||
                          (!get(form.values, 'standardAuctionAndBuyNow') &&
                            !get(form.values, 'isBuyNowFeatureEnable')) ||
                          (get(form.values, 'standardAuctionAndBuyNow') &&
                            get(form.values, 'type')?.value !==
                              AuctionStatusType.MINIMUM_PRICE &&
                            !get(form.values, 'autoAcceptPriceInPence')) ||
                          (get(form.values, 'isBuyNowFeatureEnable') &&
                            !get(form.values, 'buyNowPriceInPence')) ||
                          (get(form.values, 'type')?.value === AuctionStatusType.PHYSICAL &&
                            (!get(form.values, 'autoAcceptPriceInPence') ||
                              +get(form.values, 'autoAcceptPriceInPence') <= 0))
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <CommonButton
                        label="Cancel"
                        variant={CommonButtonVariants.OUTLINED_RED}
                        handleClick={() => setIsRelaunchPopupVisible(false)}
                        className="w-100 common-button outline-primary-red"
                      />
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </PortalSweetAlert>
    );
  };

  return (
    <div className="p-4 px-2">
      {(() => {
        switch (item.status) {
          case AuctionStatus.DRAFT: {
            const isLaunchEnabled = (isOwner || item?.seller?.id === id) && item?.isDraftValid;
            return (
              <Row
                xs="12"
                className="summary__main-description-container auction-detail-seller auction-detail-seller-draft"
              >
                <Col xs="12" className="auction-seller-section__draft-buttons-outer-col gap-2">
                  <Col
                    // sm={`${isLaunchEnabled ? 6 : 12}`}
                    className={classNames(
                      isLaunchEnabled
                        ? 'auction-seller-section__draft-buttons-wrapper--with-launch'
                        : 'auction-seller-section__draft-buttons-wrapper',
                      isMobile && 'w-100',
                    )}
                  >
                    {(isOwner || item?.seller?.id === id) && (
                      <>
                        <CommonButton
                          className={`red-button mt-0 ${
                            isLaunchEnabled ? 'order-0 mt-0' : 'order-0 mb-3 mb-sm-0'
                          } w-100`}
                          label="Delete"
                          variant={CommonButtonVariants.DELETE}
                          handleClick={() => setConfirmDelete(true)}
                        />

                        <PortalSweetAlert
                          showCancel
                          show={confirmDelete}
                          onConfirm={() => {
                            dispatch(
                              deleteAuction({
                                id: auctionId,
                                version: item?.version,
                              }),
                            );
                            setConfirmDelete(false);
                          }}
                          onCancel={() => setConfirmDelete(false)}
                          cancelBtnCssClass="common-button outline-primary-red"
                          cancelBtnText="Continue auction"
                          confirmBtnText="Delete offer"
                          confirmBtnCssClass="common-button red-button"
                          customClass="delete-offer-alert"
                          title=""
                        >
                          <div className="sweet-alert__header">
                            <span className="sweet-alert-text">Delete Offer</span>
                            <button type="button" onClick={() => setConfirmDelete(false)}>
                              <img src={closeIcon} alt="Close icon" />
                            </button>
                          </div>
                          <div className="sweet-alert__content">
                            <span>Are you sure you want to delete this vehicle?</span>
                          </div>
                        </PortalSweetAlert>
                      </>
                    )}
                  </Col>
                  <Col className={classNames(isMobile && 'w-100')}>
                    {(isOwner || item?.seller?.id === id) && (
                      <CommonButton
                        className="mt-0 w-100"
                        label="Edit"
                        variant={CommonButtonVariants.OUTLINED}
                        handleClick={() => {
                          dispatch(
                            editAuction(
                              {
                                auctionId,
                                version: item?.version,
                                status: item?.status,
                              },
                              navigate,
                            ),
                          );
                          dispatch(clearPhotosInServiceHistory());
                        }}
                      />
                    )}
                  </Col>
                  {isLaunchEnabled && (
                    <Col
                      className={classNames(
                        'auction-details__launch-button',
                        isMobile && 'w-100',
                      )}
                    >
                      <CommonButton
                        className={` common-button primary-button mt-0 ${isLaunchEnabled && 'mb-0'} w-100`}
                        label="Launch"
                        variant={CommonButtonVariants.PRIMARY}
                        handleClick={() => setIsRelaunchPopupVisible(true)}
                      />
                    </Col>
                  )}
                  {_renderLaunchOrRelaunchPopup(false)}
                </Col>
              </Row>
            );
          }
          case AuctionStatus.PUBLISHED:
          case AuctionStatus.WAITING:
            return (
              <Row
                xs={12}
                className="summary__main-description-container auction-detail-seller auction-detail-seller-published"
              >
                {item?.isCancellationAllowed && (
                  <Col
                    xs={12}
                    className="auction-details__buttons-section-wrapper--single-button"
                  >
                    <CommonButton
                      className="w-100 red-button"
                      label="Cancel offer"
                      variant={CommonButtonVariants.PRIMARY}
                      handleClick={() => setConfirmCancel(true)}
                    />
                    <PortalSweetAlert
                      showCancel
                      show={confirmCancel}
                      onConfirm={() => {
                        handleCancelAuction();
                        setConfirmCancel(false);
                      }}
                      onCancel={() => setConfirmCancel(false)}
                      cancelBtnCssClass="common-button outline-primary-red"
                      cancelBtnText="Continue auction"
                      confirmBtnText="Cancel offer"
                      confirmBtnCssClass="common-button red-button"
                      title=""
                      customClass="cancel-offer-alert"
                    >
                      <div className="sweet-alert__header">
                        <span className="sweet-alert-text">Cancel Offer</span>
                        <button type="button" onClick={() => setConfirmCancel(false)}>
                          <img src={closeIcon} alt="Close icon" />
                        </button>
                      </div>
                      <div className="sweet-alert__content">
                        <span>
                          Please confirm that you want to cancel offer for the selected
                          vehicle, before completion of the auction.
                        </span>
                      </div>
                    </PortalSweetAlert>
                  </Col>
                )}
              </Row>
            );
          case AuctionStatus.TO_ACCEPT:
            return (
              <Row className="summary__main-description-container auction-detail-seller auction-detail-seller-to-accept">
                <Col xs={6} className="mb-5">
                  <CommonButton
                    className="basic-data__button w-100"
                    label="Accept"
                    variant={CommonButtonVariants.PRIMARY}
                    handleClick={handleAcceptAuction}
                    icon={checkWhiteIcon}
                    iconAlt="Check icon"
                    iconLeft
                    disabled={!item?.isSellerAcceptanceAllowed}
                  />
                </Col>
                <Col xs={6}>
                  <CommonButton
                    className="basic-data__button red-button w-100"
                    label="Reject"
                    handleClick={handleRejectAuction}
                    variant={CommonButtonVariants.RED}
                    icon={closeWhiteIcon}
                    iconAlt="Close icon"
                    iconLeft
                    disabled={!item?.isSellerAcceptanceAllowed}
                  />
                </Col>
              </Row>
            );

          case AuctionStatus.ENDED:
          case AuctionStatus.CANCELLED:
          case AuctionStatus.REJECTED:
            return (
              <Row
                xs={12}
                className="summary__main-description-container auction-detail-seller auction-detail-seller-cancelled pe-1"
              >
                {item.isRepublishAllowed && item.isDraftValid && (
                  <Col xs={12} className="auction-details__buttons-section-wrapper gap-2">
                    <Col xs={6} className="auction-details__buttons-subsection">
                      <CommonButton
                        className="w-100"
                        label="Edit"
                        variant={CommonButtonVariants.OUTLINED}
                        handleClick={() =>
                          dispatch(
                            editAuction(
                              {
                                auctionId,
                                version: item?.version,
                                status: item?.status,
                              },
                              navigate,
                            ),
                          )
                        }
                      />
                    </Col>

                    <Col
                      xs={6}
                      className={
                        item.status === ENDED_STATUS
                          ? 'auction-details__relaunch-button--ended'
                          : 'auction-details__relaunch-button--cancelled'
                      }
                    >
                      <CommonButton
                        className="w-100"
                        label="Relaunch"
                        variant={CommonButtonVariants.PRIMARY}
                        handleClick={() => setIsRelaunchPopupVisible(true)}
                      />
                      {_renderLaunchOrRelaunchPopup(true)}
                    </Col>
                  </Col>
                )}
              </Row>
            );
          default:
            return null;
        }
      })()}
    </div>
  );
}

AuctionDetailsSellerSection.defaultProps = {
  bidNotification: '',
};

AuctionDetailsSellerSection.propTypes = {
  item: PropTypes.object.isRequired,
  refreshItem: PropTypes.func.isRequired,
  status: PropTypes.bool.isRequired,
  changeStatus: PropTypes.func.isRequired,
  bidNotification: PropTypes.string,
  isAuctionRelaunched: PropTypes.bool.isRequired,
  setIsAuctionRelaunched: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
};

export default AuctionDetailsSellerSection;
